(() => {
  function safeJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || "null");
    } catch (e) {
      return null;
    }
  }

  function toInt(v, fallback = 0) {
    const n = parseInt(String(v ?? "").trim(), 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function parseMoneyText(text) {
    if (!text) return NaN;
    const cleaned = String(text).replace(/[^\d.,-]/g, "").trim();
    if (!cleaned) return NaN;

    // 케이스 처리(천단위/소수점 구분)
    const hasDot = cleaned.includes(".");
    const hasComma = cleaned.includes(",");

    let normalized = cleaned;
    if (hasDot && hasComma) {
      // 마지막 기호가 소수점일 가능성이 큼
      if (cleaned.lastIndexOf(",") > cleaned.lastIndexOf(".")) {
        // "1.234,56" -> "1234.56"
        normalized = cleaned.replace(/\./g, "").replace(",", ".");
      } else {
        // "1,234.56" -> "1234.56"
        normalized = cleaned.replace(/,/g, "");
      }
    } else if (hasComma && !hasDot) {
      // "1,234" 같은 천단위
      const parts = cleaned.split(",");
      if (parts.length === 2 && parts[1].length === 2) {
        // "12,34" 같은 경우는 소수로 읽음
        normalized = cleaned.replace(",", ".");
      } else {
        normalized = cleaned.replace(/,/g, "");
      }
    }

    const num = parseFloat(normalized);
    return Number.isFinite(num) ? num : NaN;
  }

  function formatCurrency(amountMajor, currency, locale) {
    try {
      const loc = locale || document.documentElement.lang || "ja-JP";
      return new Intl.NumberFormat(loc, {
        style: "currency",
        currency: currency || "JPY",
      }).format(amountMajor);
    } catch (e) {
      // 최후 fallback
      const n = Math.round(amountMajor);
      return currency === "JPY" ? `￥${n}` : String(n);
    }
  }

  function getDisplayedUnitPriceMajor(productInfo) {
    // Dawn price snippet에서 현재 표시되는 “판매가” 텍스트를 읽음
    const priceRoot = productInfo.querySelector('[id^="price-"]') || productInfo.querySelector(".price");
    if (!priceRoot) return NaN;

    // sale 우선, 없으면 regular
    const saleEl =
      priceRoot.querySelector(".price__sale .price-item--sale") ||
      priceRoot.querySelector(".price__sale .price-item") ||
      priceRoot.querySelector(".price-item--sale");

    const regularEl =
      priceRoot.querySelector(".price__regular .price-item--regular") ||
      priceRoot.querySelector(".price__regular .price-item") ||
      priceRoot.querySelector(".price-item--regular");

    const txt = (saleEl && saleEl.textContent) || (regularEl && regularEl.textContent) || priceRoot.textContent;
    return parseMoneyText(txt);
  }

  function initOne(productInfo) {
    initUpload(productInfo);

    const wrap = productInfo.querySelector("[data-eunchang-option-summary]");
    if (!wrap) return;

    const sectionId = wrap.dataset.sectionId;
    const currency = wrap.dataset.currency || "JPY";
    const locale = wrap.dataset.locale || document.documentElement.lang || "ja-JP";

    const optNames = safeJson(document.getElementById(`EunchangProductOptions-${sectionId}`)) || [];
    const variants = safeJson(document.getElementById(`EunchangProductVariants-${sectionId}`)) || [];

    const summaryEl = wrap.querySelector("[data-option-summary]");
    const totalEl = wrap.querySelector("[data-total-price]");

    // variant id input (buy-buttons 내부 폼의 hidden input)
    const variantIdInput =
      productInfo.querySelector('input.product-variant-id[name="id"]') ||
      productInfo.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      productInfo.querySelector('input[name="id"]');

    // quantity input (quantity selector 블록)
    const qtyInput = productInfo.querySelector('input[name="quantity"]');

    function findVariantById(id) {
      const sid = String(id);
      return variants.find((v) => String(v.id) === sid) || null;
    }

    function computeUnitMajor(variant, displayedMajor) {
      const raw = Number(variant?.price);
      if (!Number.isFinite(raw)) return NaN;

      // displayedMajor(화면 표시가)와 raw의 스케일로 안전하게 변환
      if (Number.isFinite(displayedMajor)) {
        if (Math.abs(raw - displayedMajor) < 0.0001) return raw; // raw가 이미 major
        if (Math.abs(raw / 100 - displayedMajor) < 0.0001) return raw / 100;
        if (Math.abs(raw / 1000 - displayedMajor) < 0.0001) return raw / 1000;
      }

      // 일반적인 Shopify 관례(센트 기반) fallback
      return raw / 100;
    }

    function buildOptionSummary(variant, qty) {
      const parts = [];

      // 옵션명 + 옵션값 (Default Title 같은 값은 표시 제외)
      for (let i = 0; i < optNames.length; i++) {
        const name = String(optNames[i] ?? "").trim();
        const val = String(variant?.[`option${i + 1}`] ?? "").trim();

        if (!name || !val) continue;
        if (name.toLowerCase() === "title" && val.toLowerCase() === "default title") continue;
        if (val.toLowerCase() === "default title") continue;

        parts.push(`${name}：${val}`);
      }

      // 주문 수량 라벨: 옵션에 “数量”이 이미 있으면 중복 방지
      const hasQtyOption = optNames.some((n) => String(n ?? "").trim() === "数量");
      const qtyLabel = hasQtyOption ? "注文数量" : "数量";
      parts.push(`${qtyLabel}：${qty}`);

      return parts.join(" / ");
    }

    function update() {
      const variantId = toInt(variantIdInput?.value, 0);
      const variant = findVariantById(variantId) || variants.find((v) => v?.available) || variants[0] || null;

      const qty = Math.max(1, toInt(qtyInput?.value, 1));

      // 옵션 요약
      if (summaryEl) {
        summaryEl.textContent = buildOptionSummary(variant, qty);
      }

      // 합계 금액(옵션 선택/가격 표시와 최대한 동일하게)
      if (totalEl && variant) {
        const displayedUnitMajor = getDisplayedUnitPriceMajor(productInfo);
        const unitMajor = computeUnitMajor(variant, displayedUnitMajor);

        if (Number.isFinite(unitMajor)) {
          const totalMajor = unitMajor * qty;
          totalEl.textContent = formatCurrency(totalMajor, currency, locale);
        } else {
          // fallback: 표시가 그대로라도 보여줌
          totalEl.textContent = formatCurrency(displayedUnitMajor * qty, currency, locale);
        }
      }
    }

    // 이벤트(옵션 변경/수량 변경 모두 반영)
    let t = null;
    function scheduleUpdate() {
      if (t) cancelAnimationFrame(t);
      t = requestAnimationFrame(() => update());
    }

    productInfo.addEventListener("change", scheduleUpdate, true);
    productInfo.addEventListener("input", scheduleUpdate, true);

    // 초기 1회
    update();
  }

  function initAll(root = document) {
    root.querySelectorAll("product-info").forEach(initOne);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  // 테마 에디터 섹션 리로드 대응
  document.addEventListener("shopify:section:load", (e) => {
    initAll(e.target);
  });
})();


function initUpload(productInfo) {
  const box = productInfo.querySelector("[data-product-upload]");
  if (!box) return;

  if (box.dataset.uploadInit === "1") return;
  box.dataset.uploadInit = "1";

  const trigger = box.querySelector("[data-upload-trigger]");
  const input = box.querySelector("[data-upload-input]");
  const fileName = box.querySelector("[data-upload-name]");

  const modalId = box.dataset.uploadModalId;
  const modal = modalId
    ? document.getElementById(modalId)
    : document.querySelector("[data-upload-modal]");

  const confirmBtn = modal ? modal.querySelector("[data-upload-confirm]") : null;

  if (!trigger || !modal || !input || !confirmBtn || !fileName) return;

  function openModal() {
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.style.overflow = "hidden";
  }

  function closeModal() {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";
  }

  trigger.addEventListener("click", function () {
    openModal();
  });

  modal.addEventListener("click", function (event) {
    if (event.target.matches("[data-upload-close='1']")) {
      closeModal();
    }
  });

  confirmBtn.addEventListener("click", function () {
    closeModal();
    input.click();
  });

  input.addEventListener("change", function () {
    const file = input.files && input.files[0] ? input.files[0] : null;

    if (!file) {
      fileName.textContent = "選択されたファイル：なし";
      return;
    }

    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("20MB以下のファイルを選択してください。");
      input.value = "";
      fileName.textContent = "選択されたファイル：なし";
      return;
    }

    fileName.textContent = "選択されたファイル：" + file.name;
  });

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape" && modal.classList.contains("is-open")) {
      closeModal();
    }
  });
}